import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Flame,
  CheckCircle,
  BarChart3,
  Plus,
  Trophy,
  Star,
  Zap,
  Trash2,
  Sparkles,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Modal } from '@/components/ui/Modal';
import { Input, Textarea } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { tabService, type Habit, type HabitAchievement } from '../services/tabs';
import { toast } from 'sonner';

const HABIT_COLOR_STYLES: Record<string, { btn: string; badge: string; day: string }> = {
  blue: {
    btn: 'bg-blue-500 text-white',
    badge: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    day: 'bg-blue-500 text-white',
  },
  purple: {
    btn: 'bg-purple-500 text-white',
    badge: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
    day: 'bg-purple-500 text-white',
  },
  green: {
    btn: 'bg-emerald-500 text-white',
    badge: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    day: 'bg-emerald-500 text-white',
  },
  orange: {
    btn: 'bg-orange-500 text-white',
    badge: 'bg-orange-500/20 text-orange-300 border border-orange-500/30',
    day: 'bg-orange-500 text-white',
  },
};

const ACHIEVEMENT_STYLES: Record<
  string,
  { gradient: string; icon: typeof Trophy; glow: string }
> = {
  first_week: {
    gradient: 'from-orange-400 via-red-400 to-rose-500',
    icon: Flame,
    glow: 'shadow-orange-500/30',
  },
  dedicated: {
    gradient: 'from-violet-400 via-purple-500 to-indigo-600',
    icon: Star,
    glow: 'shadow-purple-500/30',
  },
  consistent: {
    gradient: 'from-emerald-400 via-teal-400 to-cyan-500',
    icon: CheckCircle,
    glow: 'shadow-emerald-500/30',
  },
  unstoppable: {
    gradient: 'from-yellow-300 via-amber-400 to-orange-500',
    icon: Zap,
    glow: 'shadow-amber-500/30',
  },
};

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const HABIT_CATEGORIES = ['Productivity', 'Learning', 'Focus', 'Health', 'Personal'];
const HABIT_COLORS = ['blue', 'purple', 'green', 'orange'] as const;

function colorStyle(color: string) {
  return HABIT_COLOR_STYLES[color] ?? HABIT_COLOR_STYLES.blue;
}

function todayWeekIndex(): number {
  const d = new Date().getDay();
  return d === 0 ? 6 : d - 1;
}

function canUseGraceDay(habit: Habit): boolean {
  if (habit.streak > 0 || habit.completedToday) return false;
  const todayIdx = todayWeekIndex();
  if (todayIdx <= 0) return false;
  const hadEarlier = habit.weekProgress.slice(0, todayIdx).some(Boolean);
  const missedYesterday = !habit.weekProgress[todayIdx - 1];
  return hadEarlier && missedYesterday;
}

export function HabitTracking() {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [achievements, setAchievements] = useState<HabitAchievement[]>([]);
  const [totalStreak, setTotalStreak] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newHabit, setNewHabit] = useState({
    name: '',
    goal: '',
    category: 'Productivity',
    color: 'blue' as (typeof HABIT_COLORS)[number],
  });

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [habitData, stats] = await Promise.all([
        tabService.getHabits(),
        tabService.getHabitStats(),
      ]);
      setHabits(habitData);
      setAchievements(stats.achievements);
      setTotalStreak(stats.totalStreak);
    } catch (error) {
      console.error('Failed to fetch habits:', error);
      toast.error('Failed to load habits');
      setHabits([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const updateHabitInList = (updated: Habit) => {
    setHabits((prev) => prev.map((h) => (h.id === updated.id ? updated : h)));
  };

  const toggleHabit = async (id: string) => {
    const habit = habits.find((h) => h.id === id);
    if (!habit) return;

    try {
      const updated = habit.completedToday
        ? await tabService.uncompleteHabit(id)
        : await tabService.completeHabit(id);
      updateHabitInList(updated);
      const stats = await tabService.getHabitStats();
      setAchievements(stats.achievements);
      setTotalStreak(stats.totalStreak);
      toast.success(habit.completedToday ? 'Habit unchecked' : 'Habit completed!');
    } catch {
      toast.error('Failed to update habit');
    }
  };

  const handleGraceDay = async (id: string) => {
    try {
      const updated = await tabService.graceDayHabit(id);
      updateHabitInList(updated);
      const stats = await tabService.getHabitStats();
      setAchievements(stats.achievements);
      setTotalStreak(stats.totalStreak);
      toast.success('Grace day applied — streak preserved!');
    } catch {
      toast.error('Could not apply grace day');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await tabService.deleteHabit(id);
      setHabits((prev) => prev.filter((h) => h.id !== id));
      const stats = await tabService.getHabitStats();
      setAchievements(stats.achievements);
      setTotalStreak(stats.totalStreak);
      toast.success('Habit removed');
    } catch {
      toast.error('Failed to delete habit');
    }
  };

  const handleCreate = async () => {
    if (!newHabit.name.trim()) {
      toast.error('Enter a habit name');
      return;
    }
    setSaving(true);
    try {
      const created = await tabService.createHabit({
        name: newHabit.name.trim(),
        goal: newHabit.goal.trim() || 'Daily habit',
        category: newHabit.category,
        color: newHabit.color,
      });
      setHabits((prev) => [...prev, created]);
      setShowCreateModal(false);
      setNewHabit({ name: '', goal: '', category: 'Productivity', color: 'blue' });
      toast.success('Habit created');
    } catch {
      toast.error('Failed to create habit');
    } finally {
      setSaving(false);
    }
  };

  const completedToday = habits.filter((h) => h.completedToday).length;

  return (
    <PageLayout
      title="Habit Tracking"
      description="Build better browsing habits and track your productivity streaks"
    >
      <div className="space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-khiimori-text-muted">Today&apos;s Progress</p>
                <p className="text-2xl font-bold text-khiimori-text-primary">
                  {completedToday}/{habits.length}
                </p>
              </div>
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
          </div>
          <div className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-khiimori-text-muted">Total Streak</p>
                <p className="text-2xl font-bold text-orange-400">{totalStreak}</p>
              </div>
              <Flame className="w-7 h-7 text-orange-400" />
            </div>
          </div>
          <div className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-khiimori-text-muted">Active Habits</p>
                <p className="text-2xl font-bold text-khiimori-text-primary">{habits.length}</p>
              </div>
              <BarChart3 className="w-7 h-7 text-primary-400" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-br from-primary-500/20 to-purple-500/20 border border-primary-500/30 rounded-xl p-4 flex flex-col items-center justify-center gap-1.5 text-primary-300 hover:from-primary-500/30 hover:to-purple-500/30 transition-colors"
          >
            <Plus className="w-7 h-7" />
            <span className="text-sm font-semibold">New Habit</span>
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Habits list */}
          <div className="xl:col-span-2 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
              </div>
            ) : habits.length === 0 ? (
              <div className="text-center py-12 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl">
                <Sparkles className="w-10 h-10 text-khiimori-text-muted mx-auto mb-3" />
                <p className="text-khiimori-text-primary font-medium">No habits yet</p>
                <p className="text-sm text-khiimori-text-muted mt-1">
                  Create a daily habit to start building streaks
                </p>
                <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Habit
                </Button>
              </div>
            ) : (
              habits.map((habit, idx) => {
                const styles = colorStyle(habit.color);
                return (
                  <motion.div
                    key={habit.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-4">
                      <button
                        type="button"
                        onClick={() => void toggleHabit(habit.id)}
                        className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                          habit.completedToday
                            ? styles.btn
                            : 'bg-khiimori-bg-elevated/20 text-khiimori-text-muted hover:bg-khiimori-bg-elevated/30'
                        }`}
                        aria-label={habit.completedToday ? 'Mark incomplete' : 'Mark complete'}
                      >
                        <CheckCircle className="w-6 h-6" />
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <h4 className="text-base font-semibold text-khiimori-text-primary truncate">
                              {habit.name}
                            </h4>
                            <p className="text-sm text-khiimori-text-muted truncate">{habit.goal}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-500/15 border border-orange-500/25 rounded-lg">
                              <Flame className="w-4 h-4 text-orange-400" />
                              <span className="text-sm font-bold text-orange-300">
                                {habit.streak} day{habit.streak === 1 ? '' : 's'}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => void handleDelete(habit.id)}
                              className="p-1.5 rounded-lg text-khiimori-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              aria-label="Delete habit"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs text-khiimori-text-muted">This week</span>
                          <div className="flex gap-1">
                            {DAY_LABELS.map((day, dayIdx) => (
                              <div
                                key={`${habit.id}-day-${dayIdx}`}
                                className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-semibold ${
                                  habit.weekProgress[dayIdx]
                                    ? styles.day
                                    : 'bg-khiimori-bg-elevated/20 text-khiimori-text-muted'
                                }`}
                              >
                                {day}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}>
                            {habit.category}
                          </span>
                          {canUseGraceDay(habit) ? (
                            <button
                              type="button"
                              onClick={() => void handleGraceDay(habit.id)}
                              className="text-xs text-amber-400 hover:text-amber-300 underline-offset-2 hover:underline"
                            >
                              Use grace day
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Achievements sidebar */}
          <div className="bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl p-4">
            <h3 className="text-base font-semibold text-khiimori-text-primary mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Achievements
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {achievements.map((achievement) => {
                const style = ACHIEVEMENT_STYLES[achievement.id] ?? {
                  gradient: 'from-slate-400 to-slate-600',
                  icon: Trophy,
                  glow: 'shadow-slate-500/20',
                };
                const Icon = style.icon;
                return (
                  <div
                    key={achievement.id}
                    className={`relative overflow-hidden rounded-xl p-3 text-center transition-all ${
                      achievement.unlocked
                        ? `bg-gradient-to-br ${style.gradient} shadow-lg ${style.glow}`
                        : 'bg-khiimori-bg-elevated/20 opacity-50 grayscale'
                    }`}
                  >
                    {achievement.unlocked ? (
                      <div className="absolute inset-0 bg-white/10 pointer-events-none" />
                    ) : null}
                    <Icon
                      className={`w-8 h-8 mx-auto mb-1.5 ${
                        achievement.unlocked ? 'text-white drop-shadow' : 'text-khiimori-text-muted'
                      }`}
                    />
                    <p
                      className={`text-xs font-bold mb-0.5 ${
                        achievement.unlocked ? 'text-white' : 'text-khiimori-text-secondary'
                      }`}
                    >
                      {achievement.name}
                    </p>
                    <p
                      className={`text-[10px] leading-tight ${
                        achievement.unlocked ? 'text-white/80' : 'text-khiimori-text-muted'
                      }`}
                    >
                      {achievement.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Habit"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Habit name"
            placeholder="e.g. Plan my day"
            value={newHabit.name}
            onChange={(e) => setNewHabit((prev) => ({ ...prev, name: e.target.value }))}
          />
          <Textarea
            label="Description"
            placeholder="What does completing this habit look like?"
            value={newHabit.goal}
            onChange={(e) => setNewHabit((prev) => ({ ...prev, goal: e.target.value }))}
            rows={2}
          />
          <div>
            <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {HABIT_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setNewHabit((prev) => ({ ...prev, category: cat }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    newHabit.category === cat
                      ? 'bg-primary-500/20 text-primary-300 border border-primary-500/40'
                      : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/20'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
              Color
            </label>
            <div className="flex gap-2">
              {HABIT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewHabit((prev) => ({ ...prev, color: c }))}
                  className={`w-9 h-9 rounded-full ${HABIT_COLOR_STYLES[c].btn} ${
                    newHabit.color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-khiimori-bg-primary' : ''
                  }`}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={saving}>
              {saving ? 'Creating…' : 'Create Habit'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
